// Messages notifications, web side (docs/specs/messaging-notifications-spec.md, 2026-11-03).
//
// One store per tab, started by MessagingHost for a signed-in staff account (never a patient,
// never on /403) and stopped on sign-out or when another account signs in. It holds:
// - the unread summary from GET /api/internal-messaging/unread/ ({counts, mentions, settings,
//   total, mention_total}), shared by the Navbar badge, the channel list and document.title through
//   useSyncExternalStore, so there is one fetch for all of them;
// - one /ws/notifications/ socket. Its events say *that* something changed, never what: every
//   activity / read / channels_changed refetches the summary (coalesced, REFRESH_DEBOUNCE_MS). Events
//   sent while it was down are not replayed, so every (re)connect and the tab becoming visible
//   refetch too, and a slow poll (FALLBACK_REFRESH_MS) covers a socket that is silently dead;
// - desktop notifications (the browser's Notification API, only while a tab is open: no service
//   worker, no Web Push). The server sends title/body without the message text (PHI); nothing here
//   adds any.
// Memory stays bounded: the summary is replaced, never appended to, and at most MAX_OPEN_NOTES
// notifications are kept to be closed on sign-out.

import { useSyncExternalStore } from "react";
import { fetchUnreadCounts, notificationsSocketUrl, authenticateSocket } from "../services/messagingService";
import { openMessages } from "./messagingBus";
import { isImpersonating } from "./impersonation";

export const REFRESH_DEBOUNCE_MS = 500;
export const FALLBACK_REFRESH_MS = 5 * 60 * 1000;
// Reconnect backoff: doubles from RETRY_BASE_MS to RETRY_MAX_MS, ± RETRY_JITTER of it (a deploy
// drops every tab's socket at once), and starts over once a socket has stayed up STABLE_MS.
export const RETRY_BASE_MS = 1000;
export const RETRY_MAX_MS = 60000;
const STABLE_MS = 30000;
const RETRY_JITTER = 0.2;
const MAX_OPEN_NOTES = 10;
// Another tab showing a channel says so in localStorage (see viewingKey), renewed every
// VIEWING_BEAT_MS; older than VIEWING_STALE_MS it is a tab that closed without clearing it.
const VIEWING_BEAT_MS = 20000;
const VIEWING_STALE_MS = 45000;

export const EMPTY_SUMMARY = Object.freeze({ counts: {}, mentions: {}, settings: {}, total: 0, mention_total: 0 });

const isObject = (v) => v && typeof v === "object" && !Array.isArray(v);
const count = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Math.floor(Number(v)) : 0);

// The totals as the server defines them: total = unread in channels that aren't muted (the dot,
// the bold), mention_total = mentions and DMs everywhere, muted or not (the red number).
function withTotals(counts, mentions, settings) {
  let total = 0;
  let mentionTotal = 0;
  Object.keys(counts).forEach((id) => {
    if (!settings[id]?.muted) total += counts[id];
  });
  Object.values(mentions).forEach((n) => {
    mentionTotal += n;
  });
  return { counts, mentions, settings, total, mention_total: mentionTotal };
}

// The server's answer as the store keeps it. An older server (or a database before the migration)
// sends only {counts, total}: no mentions and no settings, which is what it means.
export function normalizeSummary(raw) {
  if (!isObject(raw)) return EMPTY_SUMMARY;
  const counts = {};
  const mentions = {};
  const settings = {};
  if (isObject(raw.counts)) Object.entries(raw.counts).forEach(([id, n]) => { counts[id] = count(n); });
  if (isObject(raw.mentions)) Object.entries(raw.mentions).forEach(([id, n]) => { mentions[id] = count(n); });
  if (isObject(raw.settings)) {
    Object.entries(raw.settings).forEach(([id, s]) => {
      if (isObject(s)) settings[id] = { notify_level: s.notify_level || "default", muted: !!s.muted };
    });
  }
  return {
    counts,
    mentions,
    settings,
    total: raw.total !== undefined ? count(raw.total) : withTotals(counts, mentions, settings).total,
    mention_total: count(raw.mention_total),
  };
}

// A channel just marked read here: cleared at once, before the server's `read` event refetches.
export function summaryWithRead(summary, channelId) {
  if (!channelId || (!summary.counts[channelId] && !summary.mentions[channelId])) return summary;
  return withTotals(
    { ...summary.counts, [channelId]: 0 },
    { ...summary.mentions, [channelId]: 0 },
    summary.settings,
  );
}

// A channel's notify level / mute just saved here.
export function summaryWithSettings(summary, channelId, next) {
  if (!channelId || !isObject(next)) return summary;
  const prev = summary.settings[channelId] || { notify_level: "default", muted: false };
  const merged = {
    notify_level: next.notify_level || prev.notify_level,
    muted: next.muted !== undefined ? !!next.muted : prev.muted,
  };
  return withTotals(summary.counts, summary.mentions, { ...summary.settings, [channelId]: merged });
}

// How the socket answers a close. Render drops close codes (AGENTS.md §10), so the code is the one
// from the {"event":"closing"} message before it when there was one.
// 4401: the token was refused or expired → reconnect with a fresh one; 4403: not a staff account
// (or not allowed) → stop for good; anything else (4408, 4503, a dropped connection) → reconnect.
export function closeAction(code) {
  if (code === 4403) return "stop";
  if (code === 4401) return "refresh";
  return "retry";
}

export function retryDelay(failures, random = Math.random) {
  const backoff = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** failures);
  return Math.round(backoff * (1 + RETRY_JITTER * (2 * random() - 1)));
}

// Whether an `activity` event becomes a desktop notification in this tab. Not when the person is
// looking at that channel right now: this tab visible with Messages open on it, or another of
// their tabs saying it is (otherViewing).
export function shouldShowNotification(event, { permission, enabled = true, visible, viewingChannelId, otherViewing = false }) {
  if (!event || event.event !== "activity" || !event.notify || !event.title) return false;
  if (permission !== "granted" || !enabled) return false;
  if (visible && viewingChannelId && viewingChannelId === event.channel_id) return false;
  if (otherViewing) return false;
  return true;
}

// "(3) NotesAI" while there are mentions; restored when there are none. Strips our own prefix
// first, so a title the app sets meanwhile is kept. The browser trims document.title, so "(2) "
// on an empty title reads back as "(2)": the space after it is optional.
const TITLE_PREFIX = /^\(\d+\+?\)(?:\s+|$)/;
export function titleWithCount(title, mentionTotal) {
  const base = String(title || "").replace(TITLE_PREFIX, "");
  if (!mentionTotal) return base;
  return `(${mentionTotal > 99 ? "99+" : mentionTotal}) ${base}`;
}

// ---------------------------------------------------------------------------
// Browser bits, each guarded: no Notification API (Safari in some modes, jsdom), storage blocked.
// ---------------------------------------------------------------------------

export function notificationsSupported() {
  return typeof window !== "undefined" && typeof window.Notification === "function";
}

function readPermission() {
  if (!notificationsSupported()) return "unsupported";
  return window.Notification.permission || "default";
}

function storageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (err) {
    return null;
  }
}

function storageSet(key, value) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (err) {
    // storage blocked: the setting lasts for this page only
  }
}

const desktopOffKey = (userId) => `notesai:messages-desktop-off:${userId}`;
const promptDismissedKey = (userId) => `notesai:messages-notify-prompt-dismissed:${userId}`;
const viewingKey = (userId) => `notesai:messages-viewing:${userId}`;
const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const isVisible = () => typeof document === "undefined" || document.visibilityState !== "hidden";

// ---------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------

const initialState = () => ({
  userId: null,
  summary: EMPTY_SUMMARY,
  permission: readPermission(),
  desktopEnabled: true,
  promptDismissed: false,
});

let state = initialState();
const listeners = new Set();
const eventListeners = new Set();
let session = null;

function setState(patch) {
  state = { ...state, ...patch };
  syncTitle();
  listeners.forEach((l) => l());
}

// The one place the tab title follows the badge.
function syncTitle() {
  if (typeof document === "undefined") return;
  const next = titleWithCount(document.title, state.summary.mention_total);
  if (next !== document.title) document.title = next;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot() {
  return state;
}

// { userId, summary, permission, desktopEnabled, promptDismissed }
export function useMessagingNotifications() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Every socket event, for the Messages modal (a new channel to list, a channel deleted).
export function onMessagingEvent(cb) {
  eventListeners.add(cb);
  return () => eventListeners.delete(cb);
}

// Fetches the summary now. Overlapping fetches: only the newest answer lands.
export async function refreshUnread() {
  const s = session;
  if (!s || s.stopped) return;
  clearTimeout(s.debounce);
  s.debounce = null;
  s.seq += 1;
  const seq = s.seq;
  try {
    const raw = await fetchUnreadCounts(s.userId);
    if (session !== s || s.stopped || seq !== s.seq) return;
    setState({ summary: normalizeSummary(raw) });
  } catch (err) {
    console.warn("Messaging: unread count fetch failed", err?.message);
  }
}

// A burst of events → one fetch, REFRESH_DEBOUNCE_MS after the first. Each fetch starts after the
// event that asked for it, so it already sees that message.
export function scheduleRefresh() {
  const s = session;
  if (!s || s.stopped || s.debounce) return;
  s.debounce = setTimeout(() => {
    s.debounce = null;
    refreshUnread();
  }, REFRESH_DEBOUNCE_MS);
}

export function markChannelReadLocally(channelId) {
  if (!session) return;
  const next = summaryWithRead(state.summary, channelId);
  if (next !== state.summary) setState({ summary: next });
}

export function applyChannelSettingsLocally(channelId, settings) {
  if (!session) return;
  const next = summaryWithSettings(state.summary, channelId, settings);
  if (next !== state.summary) setState({ summary: next });
}

// What the Messages modal shows right now: a channel's thread on screen, or null. A notification
// for that channel is skipped while this tab is visible, and the other tabs are told.
export function setViewingChannel(channelId) {
  const s = session;
  if (!s) return;
  s.viewing = channelId || null;
  writeViewing(s);
}

function writeViewing(s) {
  clearInterval(s.viewingBeat);
  s.viewingBeat = null;
  const key = viewingKey(s.userId);
  if (s.viewing && isVisible() && !s.stopped) {
    const beat = () => storageSet(key, JSON.stringify({ tab: tabId, channelId: s.viewing, at: Date.now() }));
    beat();
    s.viewingBeat = setInterval(beat, VIEWING_BEAT_MS);
  } else {
    // Only our own claim is cleared: another tab may be showing a channel.
    try {
      const current = JSON.parse(storageGet(key) || "null");
      if (current && current.tab === tabId) storageSet(key, null);
    } catch (err) {
      storageSet(key, null);
    }
  }
}

function otherTabViewing(userId, channelId) {
  try {
    const current = JSON.parse(storageGet(viewingKey(userId)) || "null");
    return !!current && current.tab !== tabId && current.channelId === channelId
      && Date.now() - Number(current.at || 0) < VIEWING_STALE_MS;
  } catch (err) {
    return false;
  }
}

// Asks the browser for permission. Call it only from a click: browsers ignore (or penalize) a
// request no gesture led to. Older Safari takes a callback instead of returning a promise.
export async function requestNotificationPermission() {
  if (!notificationsSupported()) {
    setState({ permission: "unsupported" });
    return "unsupported";
  }
  let result;
  try {
    result = await new Promise((resolve) => {
      const answer = window.Notification.requestPermission(resolve);
      if (answer && typeof answer.then === "function") answer.then(resolve, () => resolve(readPermission()));
    });
  } catch (err) {
    result = readPermission();
  }
  const patch = { permission: result || readPermission() };
  if (result === "granted") {
    patch.desktopEnabled = true;
    if (state.userId) storageSet(desktopOffKey(state.userId), null);
  }
  setState(patch);
  return patch.permission;
}

// The settings panel's switch: off keeps the browser's permission but shows nothing here.
export function setDesktopEnabled(enabled) {
  if (state.userId) storageSet(desktopOffKey(state.userId), enabled ? null : "1");
  setState({ desktopEnabled: !!enabled });
}

export function dismissPermissionPrompt() {
  if (state.userId) storageSet(promptDismissedKey(state.userId), "1");
  setState({ promptDismissed: true });
}

function showNotification(s, event) {
  // A "view as" tab is an admin looking at someone else's account: their counts, yes; desktop
  // notifications of that person's messages popping up on the admin's screen, no.
  if (isImpersonating()) return;
  const ok = shouldShowNotification(event, {
    permission: readPermission(),
    enabled: state.desktopEnabled,
    visible: isVisible(),
    viewingChannelId: s.viewing,
    otherViewing: otherTabViewing(s.userId, event.channel_id),
  });
  if (!ok) return;
  let note;
  try {
    // The tag is the same in every tab, so the browser shows one per message however many tabs got it.
    note = new window.Notification(String(event.title), { body: String(event.body || ""), tag: `msg-${event.message_id}` });
  } catch (err) {
    // Chrome on Android throws for page notifications: there is nothing to show then.
    return;
  }
  const channelId = event.channel_id;
  note.onclick = () => {
    try {
      window.focus();
    } catch (err) {
      // nothing
    }
    openMessages({ tab: "chat", focus: { kind: "channel", id: channelId } });
    note.close();
  };
  note.onclose = () => s.notes.delete(note);
  s.notes.add(note);
  if (s.notes.size > MAX_OPEN_NOTES) {
    const oldest = s.notes.values().next().value;
    s.notes.delete(oldest);
    oldest.close();
  }
}

function handleSocketMessage(s, ws, raw) {
  let payload;
  try {
    payload = JSON.parse(raw);
  } catch (err) {
    console.warn("Messaging: bad notifications payload");
    return;
  }
  if (!payload || typeof payload !== "object") return;
  switch (payload.event) {
    case "ready":
      s.refreshToken = false;
      clearTimeout(s.stableTimer);
      s.stableTimer = setTimeout(() => {
        s.failures = 0;
      }, STABLE_MS);
      refreshUnread();
      return;
    case "closing":
      s.closingCode = payload.code;
      return;
    case "activity":
      scheduleRefresh();
      showNotification(s, payload);
      break;
    case "read":
    case "channels_changed":
      scheduleRefresh();
      break;
    default:
      return;
  }
  eventListeners.forEach((cb) => {
    try {
      cb(payload);
    } catch (err) {
      console.warn("Messaging: notifications listener failed", err);
    }
  });
}

function scheduleReconnect(s) {
  if (s.stopped || s.halted) return;
  clearTimeout(s.retryTimer);
  const delay = retryDelay(s.failures);
  s.failures += 1;
  s.retryTimer = setTimeout(() => {
    s.retryTimer = null;
    connect(s);
  }, delay);
}

function connect(s) {
  if (s.stopped || s.halted) return;
  let ws;
  try {
    ws = new WebSocket(notificationsSocketUrl());
  } catch (err) {
    console.warn("Messaging: notifications socket failed", err?.message);
    scheduleReconnect(s);
    return;
  }
  s.socket = ws;
  s.closingCode = null;
  ws.onopen = () => {
    authenticateSocket(ws, { refresh: s.refreshToken }).catch((err) =>
      console.warn("Messaging: notifications sign-in failed", err?.message),
    );
  };
  ws.onmessage = (event) => {
    if (s.stopped || s.socket !== ws) return;
    handleSocketMessage(s, ws, event.data);
  };
  ws.onclose = (event) => {
    if (s.stopped || s.socket !== ws) return;
    s.socket = null;
    clearTimeout(s.stableTimer);
    const action = closeAction(s.closingCode ?? event?.code);
    if (action === "stop") {
      // Not allowed a feed (not a staff account): no reconnects, no polling.
      s.halted = true;
      clearInterval(s.poll);
      return;
    }
    s.refreshToken = action === "refresh";
    scheduleReconnect(s);
  };
}

function detach(ws) {
  if (!ws) return;
  ws.onopen = null;
  ws.onmessage = null;
  ws.onclose = null;
  try {
    ws.close();
  } catch (err) {
    // already closed
  }
}

// Starts the store for this account (idempotent for the same one; another account replaces it).
// Returns the function that stops it.
export function startMessagingNotifications(userId) {
  if (!userId) return () => {};
  if (session && session.userId === userId && !session.stopped) {
    const same = session;
    return () => stopSession(same);
  }
  if (session) stopSession(session);
  const s = {
    userId,
    socket: null,
    stopped: false,
    halted: false,
    failures: 0,
    refreshToken: false,
    closingCode: null,
    retryTimer: null,
    stableTimer: null,
    debounce: null,
    seq: 0,
    viewing: null,
    viewingBeat: null,
    notes: new Set(),
    poll: null,
    onVisibility: null,
  };
  session = s;
  setState({
    userId,
    summary: EMPTY_SUMMARY,
    permission: readPermission(),
    desktopEnabled: storageGet(desktopOffKey(userId)) !== "1",
    promptDismissed: storageGet(promptDismissedKey(userId)) === "1",
  });
  s.poll = setInterval(refreshUnread, FALLBACK_REFRESH_MS);
  // Back to the tab: counts may have moved while timers were throttled, the permission may have
  // been changed in the browser's settings, and a socket waiting out its backoff tries now.
  s.onVisibility = () => {
    writeViewing(s);
    if (!isVisible() || s.stopped) return;
    setState({ permission: readPermission() });
    refreshUnread();
    if (!s.socket && s.retryTimer && !s.halted) {
      clearTimeout(s.retryTimer);
      s.retryTimer = null;
      s.failures = 0;
      connect(s);
    }
  };
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", s.onVisibility);
  refreshUnread();
  connect(s);
  return () => stopSession(s);
}

function stopSession(s) {
  if (s.stopped) return;
  s.viewing = null;
  writeViewing(s);
  s.stopped = true;
  clearTimeout(s.retryTimer);
  clearTimeout(s.stableTimer);
  clearTimeout(s.debounce);
  clearInterval(s.poll);
  clearInterval(s.viewingBeat);
  if (typeof document !== "undefined" && s.onVisibility) document.removeEventListener("visibilitychange", s.onVisibility);
  detach(s.socket);
  s.socket = null;
  // Names of the last account's colleagues don't stay on screen for the next one.
  s.notes.forEach((note) => {
    try {
      note.close();
    } catch (err) {
      // gone already
    }
  });
  s.notes.clear();
  if (session === s) {
    session = null;
    setState(initialState());
  }
}

export function stopMessagingNotifications() {
  if (session) stopSession(session);
}
